import Typography from '@mui/material/Typography'
import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import Box from '@mui/material/Box'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import { useBlockDetailsQuery } from 'shared/store/useCompanyDetails'
import { sortAlphabetical } from 'shared/utils/helpers/sorters'
import useAccountLimits from 'shared/store/useAccountLimits'
import { useProfileQuery } from 'shared/store/useProfile'
import useWholesalers from 'shared/store/useWholesalers'

import WholesaleCard from './components/WholesaleCard'
import { pl, en, de } from './locales'
import keepWholesalesService from './services'

export default function WholesalesKeeping() {
   const [wholesalesKept, setWholesalesKept] = useState<string[]>([])
   const { data: limits } = useAccountLimits()
   const { data: companyStatus } = useBlockDetailsQuery()
   const { data: useProfileData } = useProfileQuery()
   const [isLoading, setIsLoading] = useState(false)
   const navigate = useNavigate()
   const { data: wholesales } = useWholesalers(useProfileData?.locationUuid)
   const { t, i18n } = useTranslation(['TRANSLATION', 'WHOLESALE_KEEPING'])
   i18n.addResourceBundle('pl-PL', 'WHOLESALE_KEEPING', pl)
   i18n.addResourceBundle('en-EN', 'WHOLESALE_KEEPING', en)
   i18n.addResourceBundle('de-DE', 'WHOLESALE_KEEPING', de)
   const { addNotification } = useNotificationContext()

   function handleKeepWholesale(cooperationUuid: string) {
      if (wholesalesKept.includes(cooperationUuid)) {
         setWholesalesKept((prevState) => prevState.filter((item) => item !== cooperationUuid))
      } else if (limits?.wholesalesIntegrationsLimit) {
         if (wholesalesKept.length < limits.wholesalesIntegrationsLimit) {
            const newWholesales: string[] = [...wholesalesKept]
            newWholesales.push(cooperationUuid)
            setWholesalesKept([...newWholesales])
         }
      }
   }

   function onSubmit() {
      setIsLoading(true)
      keepWholesalesService(
         { actionType: 'CHOOSE_TO_KEEP', cooperationUuids: wholesalesKept },
         useProfileData!.locationUuid
      )
         .then((res) => {
            if (res.status === 204) {
               addNotification('successSave', 'success')
               setTimeout(() => {
                  window.localStorage.removeItem('searchBlocked')
                  navigate('/dashboard')
               }, 1500)
            }
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => setIsLoading(false))
   }

   useEffect(() => {
      if (companyStatus) {
         if (!companyStatus.blocked) {
            navigate('/dashboard')
         }
      }
   }, [companyStatus])

   return (
      <Box>
         <Typography variant="h4" color="primary" sx={{ fontWeight: 'normal', mb: 2 }}>
            {t('WHOLESALE_KEEPING:wholesaleChoosing')}
         </Typography>
         <Typography variant="h5" color="grayText">
            {t('WHOLESALE_KEEPING:wholesalesChoosingDesc')}
         </Typography>
         <Box
            sx={{
               mt: { lg: '24px', md: '24px', xs: '12px' },
               display: 'flex',
               gap: { lg: '16px', md: '12px', xs: '8px' },
               flexWrap: 'wrap',
            }}
         >
            {wholesales?.cooperating && limits ? (
               wholesales.cooperating
                  .sort((a, b) => sortAlphabetical(a.wholesaleName, b.wholesaleName))
                  .map((wholesale) => (
                     <WholesaleCard
                        key={wholesale.cooperationUuid}
                        disabled={
                           !wholesalesKept.includes(wholesale.cooperationUuid) &&
                           wholesalesKept.length === limits.wholesalesIntegrationsLimit
                        }
                        selected={wholesalesKept.includes(wholesale.cooperationUuid)}
                        wholesaleInfo={wholesale}
                        onClick={handleKeepWholesale}
                     />
                  ))
            ) : (
               <CircularProgress sx={{ m: '0 auto' }} />
            )}
         </Box>
         <Box sx={{ display: 'flex', justifyContent: 'center', mt: '16px' }}>
            <ContainedButton
               variant="contained"
               onClick={onSubmit}
               disabled={isLoading || wholesalesKept.length !== limits?.wholesalesIntegrationsLimit}
            >
               {t('TRANSLATION:save')}
            </ContainedButton>
         </Box>
      </Box>
   )
}
